import React, { useRef, useState, useEffect } from "react";
import api from "../api";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import PropertyDetails from "./PropertyDetails";
import ChoroplethMap from "./Heatmap";
import {
  FaClock,
  FaChartLine,
  FaSearchLocation,
  FaLightbulb,
  FaHome,
  FaPercent,
  FaUndo,
  FaBalanceScale,
  FaLandmark,
} from "react-icons/fa";

const LIBRARIES = ["places", "visualization"];

function Hero() {
  const [street, setStreet] = useState("");
  const [manualStreet, setManualStreet] = useState("");
  const [postcode, setPostcode] = useState("");
  const [unit, setUnit] = useState("");
  const [result, setResult] = useState(null);
  const [showUnitField, setShowUnitField] = useState(false);
  const [postcodeBounds, setPostcodeBounds] = useState(null);

  // Map state
  const [level, setLevel] = useState("districts"); // "districts" → "district" → "sector"
  const [code, setCode] = useState(null);
  const [geojson, setGeojson] = useState(null);
  const [flatsGeojson, setFlatsGeojson] = useState(null);

  const acRef = useRef(null);
  const streetAcRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  // =====================
  // Heatmap Fetcher
  // =====================
  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      try {
        console.log("📡 Fetching heatmap:", level, code);

        if (level === "districts") {
          const res = await api.post(`/heatmap`, { level: "districts" });
          if (!cancelled) {
            const districts =
              typeof res.data.districts === "string"
                ? JSON.parse(res.data.districts)
                : res.data.districts;

            setGeojson(districts);
            setFlatsGeojson(null);
          }
        } else if (level === "district") {
          const res = await api.post(`/heatmap`, { level: "district", code });
          if (!cancelled) {
            const sectors =
              typeof res.data.sectors === "string"
                ? JSON.parse(res.data.sectors)
                : res.data.sectors;

            setGeojson(sectors);
            setFlatsGeojson(null);
          }
        } else if (level === "sector") {
          const res = await api.post(`/heatmap`, { level: "sector", code });
          if (!cancelled) {
            const sector =
              typeof res.data.sector === "string"
                ? JSON.parse(res.data.sector)
                : res.data.sector;

            const flats =
              typeof res.data.flats === "string"
                ? JSON.parse(res.data.flats)
                : res.data.flats;

            setGeojson(sector);
            setFlatsGeojson(flats);
          }
        }

        console.log("✅ Heatmap fetch done:", level, code);
      } catch (err) {
        if (!cancelled) {
          console.error("❌ Error fetching heatmap:", {
            message: err.message,
            status: err.response?.status,
            data: err.response?.data,
          });
        }
      }
    }

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [level, code]);

  // =====================
  // Autocomplete Handlers
  // =====================
  const onLoad = (autocomplete) => {
    acRef.current = autocomplete;
  };

  const onPlaceChanged = () => {
    if (!acRef.current) return;
    const place = acRef.current.getPlace();
    if (!place.address_components) return;

    const postal = place.address_components.find((c) =>
      c.types.includes("postal_code")
    );
    const hasStreet = place.address_components.some((c) =>
      c.types.includes("route")
    );

    if (postal && !hasStreet) {
      setPostcode(postal.long_name);
      setStreet("");
      setManualStreet("");
      setShowUnitField(true);

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { address: postal.long_name, componentRestrictions: { country: "GB" } },
        (results, status) => {
          if (status === "OK" && results[0]) {
            const bounds =
              results[0].geometry.bounds || results[0].geometry.viewport;
            setPostcodeBounds(bounds);
          }
        }
      );
      return;
    }

    let streetNumber = "";
    let route = "";
    let postalCode = "";

    for (const comp of place.address_components) {
      if (comp.types.includes("street_number")) {
        streetNumber = comp.long_name;
      } else if (comp.types.includes("route")) {
        route = comp.long_name;
      } else if (comp.types.includes("postal_code")) {
        postalCode = comp.long_name;
      }
    }

    setStreet(`${streetNumber} ${route}`.trim());
    setPostcode(postalCode);
    setManualStreet("");
    setShowUnitField(true);
  };

  // =====================
  // Search Submit
  // =====================
  const handleSubmit = async (e) => {
    e.preventDefault();

    let parts = [];
    if (unit) parts.push(unit);
    if (street || manualStreet) parts.push(street || manualStreet);
    if (postcode) parts.push(postcode);

    let fullAddress = parts.join(" ");
    fullAddress = fullAddress
      .toUpperCase()
      .replace(/,/g, "")
      .replace(/\s+/g, " ")
      .trim();

    try {
      const response = await api.post("/enrich", { address: fullAddress });
      setResult(response.data);
    } catch (err) {
      console.error("Error calling enrich API:", err);
      alert("Could not fetch property data.");
    }
  };

  const resetSearch = () => {
    setResult(null);
    setStreet("");
    setManualStreet("");
    setPostcode("");
    setUnit("");
    setShowUnitField(false);
    setPostcodeBounds(null);
    setLevel("districts");
    setCode(null);
    setFlatsGeojson(null);
  };

  const resetMap = () => {
    setLevel("districts");
    setCode(null);
    setFlatsGeojson(null);
  };

  // =====================
  // Render
  // =====================
  return (
    <section className="px-6 py-12 bg-sky-50">
      <div className="max-w-7xl mx-auto flex flex-col gap-12">
        {/* Headline */}
        <div className="text-center lg:text-left">
          <h1 className="text-4xl md:text-5xl font-bold text-sky-900 leading-tight">
            AI-Powered Property Valuation & Insights
          </h1>
          <p className="mt-4 text-lg text-sky-700 max-w-2xl mx-auto lg:mx-0">
            Get instant valuations with confidence ranges and transparent
            explanations. Backed by deep analysis of UK national trends and your
            local micro-market.
          </p>
        </div>

        {/* Features + Heatmap side by side */}
        <div className="flex flex-col lg:flex-row items-stretch gap-12">
          {/* Features */}
          <div className="grid grid-cols-2 gap-8 flex-1">
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <FaClock className="text-sky-700 text-3xl mb-2" />
              <h3 className="font-semibold text-sky-900">Instant Results</h3>
              <p className="text-gray-600 text-sm">AI valuations in seconds</p>
            </div>
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <FaChartLine className="text-sky-700 text-3xl mb-2" />
              <h3 className="font-semibold text-sky-900">Confidence Range</h3>
              <p className="text-gray-600 text-sm">Know the high & low bound</p>
            </div>
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <FaLightbulb className="text-sky-700 text-3xl mb-2" />
              <h3 className="font-semibold text-sky-900">Transparency</h3>
              <p className="text-gray-600 text-sm">
                See why the price makes sense
              </p>
            </div>
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <FaSearchLocation className="text-sky-700 text-3xl mb-2" />
              <h3 className="font-semibold text-sky-900">Local Trends</h3>
              <p className="text-gray-600 text-sm">Understand the local market</p>
            </div>
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <FaHome className="text-sky-700 text-3xl mb-2" />
              <h3 className="font-semibold text-sky-900">Similar Properties</h3>
              <p className="text-gray-600 text-sm">
                Compare with recent sales nearby
              </p>
            </div>
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <FaPercent className="text-sky-700 text-3xl mb-2" />
              <h3 className="font-semibold text-sky-900">
                Mortgage & Rental Yield
              </h3>
              <p className="text-gray-600 text-sm">
                Affordability & investment returns
              </p>
            </div>

            {/* New: Negotiation Tools */}
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <FaBalanceScale className="text-sky-700 text-3xl mb-2" />
              <h3 className="font-semibold text-sky-900">Negotiation Tools</h3>
              <p className="text-gray-600 text-sm">
                Get the right tools to negotiate with confidence
              </p>
            </div>

            {/* New: Independent Benchmark */}
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <FaLandmark className="text-sky-700 text-3xl mb-2" />
              <h3 className="font-semibold text-sky-900">
                Independent Benchmark
              </h3>
              <p className="text-gray-600 text-sm">
                Get an independent benchmark based on real past sales
              </p>
            </div>
          </div>

          {/* Map container */}
          <div className="flex-1 flex flex-col">
            {/* Breadcrumb navigation */}
            <div className="mb-2 flex justify-between items-center">
              <div className="text-sm text-sky-800">
                <span
                  className="cursor-pointer hover:underline"
                  onClick={() => {
                    setLevel("districts");
                    setCode(null);
                    setFlatsGeojson(null);
                  }}
                >
                  All Districts
                </span>
                {level !== "districts" && (
                  <>
                    {" > "}
                    <span
                      className="cursor-pointer hover:underline"
                      onClick={() => {
                        if (level === "sector") {
                          setLevel("district");
                          setCode(code ? code.slice(0, 3) : null);
                          setFlatsGeojson(null);
                        }
                      }}
                    >
                      {level === "district" || level === "sector"
                        ? `District ${code}`
                        : ""}
                    </span>
                  </>
                )}
                {level === "sector" && (
                  <>
                    {" > "}
                    <span className="text-gray-500">{`Sector ${code}`}</span>
                  </>
                )}
              </div>

              {/* Reset button */}
              <button
                onClick={resetMap}
                className="flex items-center gap-1 px-3 py-1 text-sm bg-sky-100 text-sky-800 rounded hover:bg-sky-200"
              >
                <FaUndo /> Reset
              </button>
            </div>

            {/* Heatmap */}
            <div className="flex-1 min-h-[400px]">
              <ChoroplethMap
                geojson={geojson}
                flats={flatsGeojson}
                isLoaded={isLoaded}
                level={level}
                onFeatureClick={(newCode) => {
                  if (level === "districts") {
                    setLevel("district");
                    setCode(newCode);
                  } else if (level === "district") {
                    setLevel("sector");
                    setCode(newCode);
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Search form */}
        {!result && (
          <form
            onSubmit={handleSubmit}
            className="mt-10 w-full max-w-xl mx-auto space-y-4"
          >
            {isLoaded && (
              <Autocomplete
                onLoad={onLoad}
                onPlaceChanged={onPlaceChanged}
                options={{
                  componentRestrictions: { country: ["gb"] },
                  types: ["geocode"],
                  fields: ["address_components", "geometry"],
                }}
              >
                <input
                  type="text"
                  placeholder="Enter postcode or address…"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 text-gray-700 focus:outline-none shadow-sm"
                />
              </Autocomplete>
            )}

            {postcode && !street && isLoaded && (
              <Autocomplete
                onLoad={(autocomplete) => {
                  streetAcRef.current = autocomplete;
                  if (postcodeBounds && autocomplete) {
                    autocomplete.setBounds(postcodeBounds);
                  }
                }}
                onPlaceChanged={() => {
                  if (!streetAcRef.current) return;
                  const place = streetAcRef.current.getPlace();
                  if (!place || !place.address_components) return;

                  let streetNumber = "";
                  let route = "";

                  for (const comp of place.address_components) {
                    if (comp.types.includes("street_number")) {
                      streetNumber = comp.long_name;
                    } else if (comp.types.includes("route")) {
                      route = comp.long_name;
                    }
                  }

                  setManualStreet(`${streetNumber} ${route}`.trim());
                }}
                options={{
                  componentRestrictions: { country: ["gb"] },
                  types: ["address"],
                  fields: ["address_components", "geometry"],
                }}
              >
                <input
                  type="text"
                  placeholder="Enter street name"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 text-gray-700 focus:outline-none shadow-sm"
                  value={manualStreet}
                  onChange={(e) => setManualStreet(e.target.value)}
                />
              </Autocomplete>
            )}

            {showUnitField && (
              <input
                type="text"
                placeholder="Flat / House number / Building name"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 text-gray-700 focus:outline-none shadow-sm"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
              />
            )}

            <button
              type="submit"
              className="w-full px-6 py-3 bg-sky-800 text-white font-medium rounded-xl hover:bg-sky-900"
            >
              Get Free Valuation
            </button>
          </form>
        )}

        {result && <PropertyDetails data={result} onNewSearch={resetSearch} />}
      </div>
    </section>
  );
}

export default Hero;
